"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { FiCalendar } from "react-icons/fi"
import FormInput from "../../../ui/form-input"
import CustomSelect from "../../../ui/custom-select"
import FormCheckbox from "../../../ui/form-checkbox"
import api from "../../../libs/axios"
import toast from "react-hot-toast"
import ModalSelect from "../../../ui/select-modal"

export enum PitchType {
  ROUGH = "rough",
  CEMENT = "cement",
  TURF = "turf",
  MATTING = "matting",
  ASTROTURF = "astroturf",
}

const matchTypes = [
  { value: "practice", label: "Practice Match", description: "Training or practice game" },
  { value: "friendly", label: "Friendly Match", description: "Casual match between teams" },
  { value: "tournament", label: "Tournament Match", description: "Official tournament game" },
  { value: "league", label: "League Match", description: "League competition match" },
]

const ballTypes = [
  { value: "leather", label: "Leather Ball", description: "Traditional leather cricket ball" },
  { value: "tennis", label: "Tennis Ball", description: "Tennis ball for casual play" },
  { value: "other", label: "Other", description: "Other type of ball" },
]

const pitchTypes = [
  { value: PitchType.ROUGH, label: "Rough Pitch", description: "Rough surface" },
  { value: PitchType.CEMENT, label: "Cement Pitch", description: "Cement surface" },
  { value: PitchType.TURF, label: "Turf Pitch", description: "Natural turf surface" },
  { value: PitchType.MATTING, label: "Matting Pitch", description: "Matting surface" },
  { value: PitchType.ASTROTURF, label: "Astroturf Pitch", description: "Artificial grass surface" },
]

interface Props {
  onComplete: (data: any) => void
  matchData: any
}

export default function CreateMatch({ onComplete, matchData }: Props) {
  // Determine if this is edit mode or create mode
  const isEditMode = Boolean(matchData?.match_id)

  const [formData, setFormData] = useState({
    title: matchData.title || "",
    match_type: matchData.match_type?.toString() || "",
    tournament_id: matchData.tournament_id?.toString() || "",
    match_date: matchData.match_date || "",
    ground_id: matchData.ground_id?.toString() || "",
    city: matchData.city || "",
    ground_latitude: matchData.ground_latitude?.toString() || "",
    ground_longitude: matchData.ground_longitude?.toString() || "",
    ball_type: matchData.ball_type?.toString() || "",
    total_overs: matchData.total_overs?.toString() || "",
    organizer_id: matchData.organizer_id?.toString() || "",
    officials_umpires: matchData.officials_umpires || false,
    officials_scorer: matchData.officials_scorer || false,
    officials_live_streamer: matchData.officials_live_streamer || false,
    officials_others: matchData.officials_others || false,
    powerplay_enabled: matchData.powerplay_enabled || false,
    powerplay_overs: matchData.powerplay_overs?.toString() || "",
    pitch_type: matchData.pitch_type?.toString() || "",
    max_overs_per_bowler: matchData.max_overs_per_bowler?.toString() || "",
  })

  const [errors, setErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  // helper to format ISO date to datetime-local value (YYYY-MM-DDTHH:MM)
  const toDatetimeLocal = (iso?: string) => {
    if (!iso) return ""
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    const yyyy = d.getFullYear()
    const mm = pad(d.getMonth() + 1)
    const dd = pad(d.getDate())
    const hh = pad(d.getHours())
    const min = pad(d.getMinutes())
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`
  }

  useEffect(() => {
    // When async matchData arrives (edit mode), sync the form values
    if (!matchData) return
    setFormData((prev) => ({
      ...prev,
      title: matchData.title ?? "",
      match_type: matchData.match_type?.toString() ?? "",
      tournament_id: matchData.tournament_id?.toString() ?? "",
      match_date: toDatetimeLocal(matchData.match_date) || prev.match_date,
      ground_id: matchData.ground_id?.toString() ?? "",
      city: matchData.city ?? "",
      ground_latitude: matchData.ground_latitude != null ? matchData.ground_latitude.toString() : "",
      ground_longitude: matchData.ground_longitude != null ? matchData.ground_longitude.toString() : "",
      ball_type: matchData.ball_type?.toString() ?? "",
      total_overs: matchData.total_overs != null ? matchData.total_overs.toString() : "",
      organizer_id: matchData.organizer_id?.toString() ?? "",
      officials_umpires: matchData.officials_umpires ?? false,
      officials_scorer: matchData.officials_scorer ?? false,
      officials_live_streamer: matchData.officials_live_streamer ?? false,
      officials_others: matchData.officials_others ?? false,
      powerplay_enabled: matchData.powerplay_enabled ?? false,
      powerplay_overs: matchData.powerplay_overs != null ? matchData.powerplay_overs.toString() : "",
      pitch_type: matchData.pitch_type?.toString() ?? "",
      max_overs_per_bowler: matchData.max_overs_per_bowler != null ? matchData.max_overs_per_bowler.toString() : "",
    }))
  }, [matchData])

  const validateForm = () => {
    const newErrors: any = {}

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = "Match title is required"
    } else if (formData.title.length < 3 || formData.title.length > 200) {
      newErrors.title = "Match title must be between 3-200 characters"
    }

    // Match type validation
    if (!formData.match_type) {
      newErrors.match_type = "Match type is required"
    }

    // Organizer validation
    if (!formData.organizer_id) {
      newErrors.organizer_id = "Match organizer is required"
    }

    if (!formData.pitch_type) {
      newErrors.pitch_type = "Pitch type is required"
    }

    // Match date validation
    if (!formData.match_date) {
      newErrors.match_date = "Match date is required"
    } else {
      const matchDate = new Date(formData.match_date)
      const now = new Date()
      if (!isEditMode && matchDate < now) {
        newErrors.match_date = "Match date cannot be in the past"
      }
    }

    // Ground name validation
    if (!formData.ground_id) {
      newErrors.ground_id = "Ground name is required"
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = "City is required"
    }

    // Ball type validation
    if (!formData.ball_type) {
      newErrors.ball_type = "Ball type is required"
    }

    // Total overs validation
    if (!formData.total_overs) {
      newErrors.total_overs = "Total overs is required"
    } else {
      const overs = Number.parseInt(formData.total_overs.toString())
      if (overs < 1 || overs > 50) {
        newErrors.total_overs = "Total overs must be between 1 and 50"
      }
    }

    // Powerplay validation
    if (formData.powerplay_enabled && !formData.powerplay_overs) {
      newErrors.powerplay_overs = "Powerplay overs is required when enabled"
    } else if (formData.powerplay_enabled && formData.powerplay_overs) {
      const powerplayOvers = Number.parseInt(formData.powerplay_overs.toString())
      const totalOvers = Number.parseInt(formData.total_overs.toString())
      if (powerplayOvers >= totalOvers) {
        newErrors.powerplay_overs = "Powerplay overs must be less than total overs"
      }
    }

    // Max overs per bowler validation
    if (formData.max_overs_per_bowler) {
      const maxOvers = Number.parseInt(formData.max_overs_per_bowler.toString())
      const totalOvers = Number.parseInt(formData.total_overs.toString())
      if (maxOvers > totalOvers) {
        newErrors.max_overs_per_bowler = "Max overs per bowler cannot exceed total overs"
      }
    }

    // Latitude validation
    if (formData.ground_latitude) {
      const lat = Number.parseFloat(formData.ground_latitude.toString())
      if (lat < -90 || lat > 90) {
        newErrors.ground_latitude = "Latitude must be between -90 and 90"
      }
    }

    // Longitude validation
    if (formData.ground_longitude) {
      const lng = Number.parseFloat(formData.ground_longitude.toString())
      if (lng < -180 || lng > 180) {
        newErrors.ground_longitude = "Longitude must be between -180 and 180"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const payload: any = {
        title: formData.title,
        match_type: formData.match_type,
        match_date: new Date(formData.match_date).toISOString(),
        ground_id: formData.ground_id.toString(),
        city: formData.city,
        ball_type: formData.ball_type,
        total_overs: Number.parseInt(formData.total_overs.toString()),
        organizer_id: Number.parseInt(formData.organizer_id.toString()),
        officials_umpires: formData.officials_umpires,
        officials_scorer: formData.officials_scorer,
        officials_live_streamer: formData.officials_live_streamer,
        officials_others: formData.officials_others,
      }

      if (formData.powerplay_enabled && formData.powerplay_overs) {
        payload.powerplay_enabled = formData.powerplay_enabled
        payload.powerplay_overs = Number.parseInt(formData.powerplay_overs.toString())
      }

      if (formData.pitch_type) {
        payload.pitch_type = formData.pitch_type
      }

      if (formData.max_overs_per_bowler) {
        payload.max_overs_per_bowler = Number.parseInt(formData.max_overs_per_bowler.toString())
      }

      if (formData.ground_latitude) {
        payload.ground_latitude = Number.parseFloat(formData.ground_latitude.toString())
      }

      if (formData.ground_longitude) {
        payload.ground_longitude = Number.parseFloat(formData.ground_longitude.toString())
      }

      if (formData.tournament_id) {
        payload.tournament_id = Number.parseInt(formData.tournament_id.toString())
      }

      let response

      if (isEditMode) {
        response = await api.put(`/matches/${matchData.match_id}/update-setup`, payload)
        toast.success("Match updated successfully")
      } else {
        response = await api.post("/matches", payload)
        toast.success("Match created successfully")
      }

      const result = response.data
      const matchId = isEditMode ? matchData.match_id : result.match.id

      onComplete({ ...formData, match_id: matchId })
    } catch (error: any) {
      console.error("Error:", error)
      const errorMessage =
        error.response?.data?.message || `Network Error: ${error.message || "Could not connect to the server."}`
      setApiError(errorMessage)
      toast.error(isEditMode ? "Failed to update match" : "Failed to create match")
    } finally {
      setLoading(false)
    }
  }

  // Fetch organizers with pagination
  const fetchOrganizers = async (page: number, search?: string, limit = 10, organizerId?: string | number) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search ? { search } : {}),
        ...(organizerId ? { organizer_id: organizerId.toString() } : {}),
      })

      const response = await api.get(`/users/organizers?${params}`)
      const organizersData = response.data?.data || []
      const paginationData = response.data?.pagination

      const data = organizersData.map((org: any) => {
        const firstName = org.details?.first_name || ""
        const lastName = org.details?.last_name || ""
        const fullName = `${firstName} ${lastName}`.trim()

        return {
          value: org.id.toString(),
          label: fullName || org.phone_number || `User ${org.id}`,
          description: org.phone_number,
          data: org,
        }
      })

      const pagination = {
        current_page: paginationData?.current_page || page,
        per_page: paginationData?.per_page || limit,
        total: paginationData?.total || 0,
        total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
        has_next_page: paginationData?.has_next_page || false,
        has_prev_page: paginationData?.has_prev_page || page > 1,
        next_page: paginationData?.next_page || null,
        prev_page: paginationData?.prev_page || null,
      }

      return { data, pagination }
    } catch (error) {
      console.error("Error fetching organizers:", error)

      return {
        data: [],
        pagination: {
          current_page: page,
          per_page: limit,
          total: 0,
          total_pages: 0,
          has_next_page: false,
          has_prev_page: page > 1,
          next_page: null,
          prev_page: page > 1 ? page - 1 : null,
        },
      }
    }
  }

  // Fetch tournaments with pagination
  const fetchTournaments = async (page: number, search?: string, limit = 10, tournamentId?: string | number) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search ? { search } : {}),
        ...(tournamentId ? { tournament_id: tournamentId.toString() } : {}),
      })

      const response = await api.get(`/tournaments/name?${params}`)

      const tournamentsData = response.data?.data || []
      const paginationData = response.data?.pagination

      const data = tournamentsData.map((tournament: any) => ({
        value: tournament.id.toString(),
        label: tournament.name,
        description: `Organizer: ${tournament.organizer?.details?.first_name} ${tournament.organizer?.details?.last_name} • Ground: ${tournament.ground?.name}`,
        data: tournament,
      }))

      const pagination = {
        current_page: paginationData?.current_page || page,
        per_page: paginationData?.per_page || limit,
        total: paginationData?.total || 0,
        total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
        has_next_page: paginationData?.has_next_page || false,
        has_prev_page: paginationData?.has_prev_page || page > 1,
        next_page: paginationData?.next_page || null,
        prev_page: paginationData?.prev_page || null,
      }

      return { data, pagination }
    } catch (error: any) {
      console.error("Error fetching tournaments:", error)
      throw error
    }
  }

  // Fetch grounds with pagination
  const fetchGrounds = async (page: number, search?: string, limit = 10, groundId?: string | number) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search ? { search } : {}),
        ...(groundId ? { ground_id: groundId.toString() } : {}),
      })

      const response = await api.get(`/grounds/name?${params}`)

      const groundsData = response.data?.data?.data || response.data?.data || []
      const paginationData = response.data?.data?.pagination || response.data?.pagination

      const data = groundsData.map((ground: any) => ({
        value: ground.id.toString(),
        label: ground.name,
        description: `${ground.city || "No city"} • ${ground.pitch_type || "No pitch type"}`,
        data: ground,
      }))

      const pagination = {
        current_page: paginationData?.current_page || page,
        per_page: paginationData?.per_page || limit,
        total: paginationData?.total || 0,
        total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
        has_next_page: paginationData?.has_next_page || false,
        has_prev_page: paginationData?.has_prev_page || page > 1,
        next_page: paginationData?.next_page || null,
        prev_page: paginationData?.prev_page || null,
      }

      return { data, pagination }
    } catch (error: any) {
      console.error("Error fetching grounds:", error)
      throw error
    }
  }

  return (
    <div className="space-y-6">
      {/* Header showing edit or create mode */}
      <div className="border-b pb-4">
        <h2 className="text-xl font-semibold text-gray-900">{isEditMode ? "Edit Match Setup" : "Create New Match"}</h2>
        {isEditMode && (
          <p className="text-sm text-gray-600 mt-1">
            You are editing match: <span className="font-medium">{matchData.title}</span>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <FormInput
              label="Match Title"
              value={formData.title}
              onChange={(value) => setFormData((prev) => ({ ...prev, title: value }))}
              placeholder="Enter match title"
              error={errors.title}
              required
            />
          </div>

          <CustomSelect
            label="Match Type"
            options={matchTypes}
            value={formData.match_type}
            onChange={(value) => setFormData((prev) => ({ ...prev, match_type: value }))}
            placeholder="Select match type"
            error={errors.match_type}
            required
            searchable
          />

          {/* Organizer Selection for non-tournament matches */}
          {formData.match_type !== "tournament" && (
            <ModalSelect
              label="Select Organizer"
              placeholder="Choose an organizer"
              value={formData.organizer_id}
              onChange={(option) => {
                setFormData((prev) => ({ ...prev, organizer_id: option.value }))
              }}
              fetchOptions={(page, search, limit) => fetchOrganizers(page, search, limit, formData?.organizer_id)}
              required
              searchPlaceholder="Search by name or phone..."
              error={errors.organizer_id}
              renderOption={(option) => (
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500 flex items-center mt-0.5">{option.description}</div>
                </div>
              )}
            />
          )}

          {/* Tournament Selection for tournament matches */}
          {formData.match_type === "tournament" && (
            <ModalSelect
              label="Select Tournament"
              placeholder="Choose a tournament"
              value={formData.tournament_id}
              onChange={(option) => {
                setFormData((prev) => ({
                  ...prev,
                  tournament_id: option.value,
                  organizer_id: option.data?.organizer_id || option.data?.organizer?.id,
                }))
              }}
              fetchOptions={(page, search, limit) => fetchTournaments(page, search, limit, formData?.tournament_id)}
              required
              searchPlaceholder="Search tournaments..."
              error={errors.tournament_id}
              renderOption={(option) => (
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500 flex items-center mt-0.5">{option.description}</div>
                </div>
              )}
            />
          )}

          <div className="relative">
            <FormInput
              label="Match Date"
              type="datetime-local"
              value={formData.match_date}
              onChange={(value) => setFormData((prev) => ({ ...prev, match_date: value }))}
              error={errors.match_date}
              required
              // Allow past dates in edit mode while keeping restriction for create
              min={isEditMode ? undefined : new Date().toISOString().slice(0, 16)}
              max="2099-12-31T23:59"
            />
            <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
          </div>

          {/* Ground Selection with ModalSelect */}
          <ModalSelect
            label="Select Ground"
            placeholder="Choose a ground"
            value={formData.ground_id}
            onChange={(option) => {
              const selectedGround = option.data
              setFormData((prev) => ({
                ...prev,
                ground_id: option.value,
                city: selectedGround?.city || "",
                pitch_type: selectedGround?.pitch_type || prev.pitch_type,
              }))
            }}
            fetchOptions={(page, search, limit) => fetchGrounds(page, search, limit, formData?.ground_id)}
            required
            searchPlaceholder="Search grounds..."
            error={errors.ground_id}
            renderOption={(option) => (
              <div>
                <div className="font-medium text-gray-900">{option.label}</div>
                <div className="text-sm text-gray-500 capitalize flex items-center mt-0.5">{option.description}</div>
              </div>
            )}
          />

          <FormInput
            label="City"
            value={formData.city}
            onChange={(value) => setFormData((prev) => ({ ...prev, city: value }))}
            placeholder="Enter city"
            error={errors.city}
            required
          />

          <CustomSelect
            label="Ball Type"
            options={ballTypes}
            value={formData.ball_type}
            onChange={(value) => setFormData((prev) => ({ ...prev, ball_type: value }))}
            placeholder="Select ball type"
            error={errors.ball_type}
            required
            searchable
          />

          <FormInput
            label="Total Overs"
            type="number"
            value={formData.total_overs}
            onChange={(value) => setFormData((prev) => ({ ...prev, total_overs: value }))}
            placeholder="Enter total overs"
            error={errors.total_overs}
            required
            min={1}
            max={50}
          />

          <FormInput
            label="Max Overs per Bowler"
            type="number"
            value={formData.max_overs_per_bowler}
            onChange={(value) => setFormData((prev) => ({ ...prev, max_overs_per_bowler: value }))}
            placeholder="Enter max overs per bowler"
            error={errors.max_overs_per_bowler}
            min={1}
          />

          <CustomSelect
            label="Pitch Type"
            options={pitchTypes}
            value={formData.pitch_type}
            onChange={(value) => setFormData((prev) => ({ ...prev, pitch_type: value }))}
            placeholder="Select pitch type"
            error={errors.pitch_type}
            required
            searchable
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FormCheckbox
              label="Enable Powerplay"
              checked={formData.powerplay_enabled}
              onChange={(checked) => setFormData((prev) => ({ ...prev, powerplay_enabled: checked }))}
              description="Special fielding restrictions"
            />

            {formData.powerplay_enabled && (
              <FormInput
                label="Powerplay Overs"
                type="number"
                value={formData.powerplay_overs}
                onChange={(value) => setFormData((prev) => ({ ...prev, powerplay_overs: value }))}
                placeholder="Enter powerplay overs"
                error={errors.powerplay_overs}
                min={1}
              />
            )}
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Match Officials</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormCheckbox
              label="Umpires Required"
              checked={formData.officials_umpires}
              onChange={(checked) => setFormData((prev) => ({ ...prev, officials_umpires: checked }))}
              description="Professional umpires for the match"
            />
            <FormCheckbox
              label="Scorer Required"
              checked={formData.officials_scorer}
              onChange={(checked) => setFormData((prev) => ({ ...prev, officials_scorer: checked }))}
              description="Official scorer for match statistics"
            />
            <FormCheckbox
              label="Live Streamer Required"
              checked={formData.officials_live_streamer}
              onChange={(checked) => setFormData((prev) => ({ ...prev, officials_live_streamer: checked }))}
              description="Live streaming of the match"
            />
            <FormCheckbox
              label="Other Officials"
              checked={formData.officials_others}
              onChange={(checked) => setFormData((prev) => ({ ...prev, officials_others: checked }))}
              description="Additional match officials"
            />
          </div>
        </div>

        {apiError && (
          <div className="p-3 bg-red-100 border border-red-300 text-red-800 rounded-md text-sm">{apiError}</div>
        )}

        <div className="flex justify-end pt-6 border-t">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? isEditMode
                ? "Updating Match..."
                : "Creating Match..."
              : isEditMode
                ? "Update Match"
                : "Continue to Player Selection"}
          </button>
        </div>
      </form>
    </div>
  )
}
